import { useState, useEffect, useCallback } from "react";
import { Session } from "@supabase/supabase-js";

import FullScreenLoader from "./components/molecules/Loader/Loader";
import MainPage from "./components/pages/MainPage/MainPage";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import {
  deleteUser,
  firstLogin,
  getMe,
  updateUserSettings,
} from "./services/api/user.api";
import FirstLoginPage from "./components/pages/FirstLoginPage/FirstLoginPage";
import { setUser, UserType } from "./redux/reducers/settingsReducer";
import DBLoadingPage from "./components/pages/DBLoadingPage/DBLoadingPage";
import { supabase } from "./lib/supabase/supabaseClient";
import i18n from "./i18n/i18n";

const App = () => {
  const dispatch = useDispatch();
  const [session, setSession] = useState<any>(null);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDbLoading, setIsDbLoading] = useState(false);
  const [isDoneLoadingDB, setIsDoneLoadingDB] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);

  useEffect(() => {
    const body = document.getElementsByTagName("body")[0];
    body.setAttribute("data-theme", currentUser?.theme ?? "light");
    body.setAttribute("data-font", currentUser?.fontSize ?? "normal");
  }, [currentUser]);

  const handleUserCreate = useCallback(
    async (user: UserType) => {
      const setUserSettings = async (user: UserType) => {
        localStorage.setItem("theme", user.theme ?? "light");
        localStorage.setItem("fontSize", user.fontSize ?? "normal");
        if (user.theme === null && user.fontSize === null) {
          let theme: "light" | "dark" = "light";
          if (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
          ) {
            theme = "dark";
          }
          await updateUserSettings(dispatch, theme, "normal");
          return;
        }
        if (user.theme === null) {
          if (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
          ) {
            await updateUserSettings(dispatch, "dark");
            return;
          }
          await updateUserSettings(dispatch, "light");
          return;
        }
        if (user.fontSize === null) {
          await updateUserSettings(dispatch, undefined, "normal");
          return;
        }
      };
      setIsDbLoading(true);
      await setUserSettings(user);
      firstLogin(dispatch, user.type).then((response) => {
        if (response !== undefined && response.result === "Done") {
          setIsFirstLogin(false);
          setIsDoneLoadingDB(true);
          window.location.reload();
        } else {
          setIsDbLoading(false);
        }
      });
    },
    [dispatch],
  );

  useEffect(() => {
    const checkIsFirstLogin = async (session: Session | null) => {
      if (session !== null && session.user.id !== undefined) {
        getMe(dispatch).then((user: UserType) => {
          if (user === undefined || user === null) {
            setIsFirstLogin(true);
          } else {
            dispatch(setUser(user));
            setCurrentUser(user);
            i18n.changeLanguage(user.lang ?? "sk");
            localStorage.setItem("lang", user.lang ?? "sk");

            if (
              user.subscription === "free" &&
              new Date(user.trialPeriodEndAt) < new Date()
            ) {
              setIsFirstLogin(true);
              return;
            }
            if (user.type && user.name && user.createdAt === null) {
              handleUserCreate(user);
            }
          }
        });
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session && session.access_token) {
        Cookies.set("access_token", session?.access_token, {
          expires: 7,
          secure: true,
          sameSite: "strict",
        });
        checkIsFirstLogin(session);
      }
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === "SIGNED_OUT") {
        setSession(session);
      }
      if (_event === "SIGNED_IN" && session && session.access_token) {
        const currentCookie = Cookies.get("access_token");
        if (currentCookie !== session?.access_token) {
          Cookies.set("access_token", session?.access_token, {
            expires: 7,
            secure: true,
            sameSite: "strict",
          });
          checkIsFirstLogin(session);
          setSession(session);
        }
      }
      if (_event === "TOKEN_REFRESHED" && session && session.access_token) {
        const currentCookie = Cookies.get("access_token");
        if (currentCookie !== session?.access_token) {
          Cookies.set("access_token", session?.access_token, {
            expires: 7,
            secure: true,
            sameSite: "strict",
          });
          checkIsFirstLogin(session);
          setSession(session);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch, handleUserCreate]);

  const handleLogout = async (deleteUserData?: boolean) => {
    if (deleteUserData) {
      deleteUser(dispatch).then(async (response) => {
        if (response !== undefined && response.result === "Done") {
          localStorage.clear();
          Cookies.remove("access_token");
          await supabase.auth.signOut();
          window.location.href = "/login";
        }
      });
    } else {
      localStorage.clear();
      Cookies.remove("access_token");
      await supabase.auth.signOut();
      window.location.href = "/login";
    }
  };

  const handleDoneLoadingDB = () => {
    setIsDbLoading(false);
  };

  if (isDbLoading) {
    return (
      <DBLoadingPage isDone={isDoneLoadingDB} onDone={handleDoneLoadingDB} />
    );
  }
  if (isLoading) {
    return <FullScreenLoader />;
  }
  if (!session) {
    window.location.href = "/login";
    return <></>;
  } else {
    if (isFirstLogin) {
      return (
        <FirstLoginPage
          email={session.user.email}
          onUserCreate={handleUserCreate}
          onLogout={handleLogout}
        />
      );
    }
    return (
      <>
        <FullScreenLoader />
        <MainPage onLogout={handleLogout} user={currentUser} />
      </>
    );
  }
};

export default App;
